import { VALIDATION_MESSAGES } from '@zorro/common-utils';
import {
  hasDisguisedExtension,
  isFileNameUsed,
  splitFileName,
} from '@zorro/design-system/v2/DocumentCard';
import { string } from 'yup';

export { isFileNameUsed };

/**
 * The editable part of a file name. The extension is pinned beside the input
 * rather than inside it, so it can never be edited away or swapped for another
 * file type — the same rule `DocumentCard`'s inline rename applies.
 */
export const getFileNameStem = (fileName?: string): string =>
  fileName ? splitFileName(fileName)[0] : '';

/** The pinned suffix, dot included, or empty when the file has no type. */
export const getFileNameExtension = (fileName: string): string =>
  splitFileName(fileName)[1];

/** Puts the stem back together with the extension the file arrived with. */
export const buildFileName = (stem: string, originalFileName: string): string =>
  `${stem.trim()}${splitFileName(originalFileName)[1]}`;

/**
 * The form's file-name field rule. `ownFileName` is the stored document's
 * name; a replacement upload sitting beside the field (`context.parent`)
 * takes over as the extension reference. The disguised-extension rule only
 * bites when the reference has no extension to pin — with one pinned, dots in
 * the stem are literal text.
 */
export const getFileNameSchema = (
  ownFileName: string,
  existingFileNames?: string[]
) =>
  string()
    .trim()
    .required(VALIDATION_MESSAGES.fileNameRequired)
    .test(
      'is-not-taken',
      VALIDATION_MESSAGES.fileNameAlreadyExists,
      (stem, context) => {
        const parent: { document?: File } = context.parent;
        const referenceFileName = parent.document?.name ?? ownFileName;

        return !isFileNameUsed(
          buildFileName(stem, referenceFileName),
          ownFileName,
          existingFileNames
        );
      }
    )
    .test(
      'no-disguised-extension',
      VALIDATION_MESSAGES.fileNameCannotEndWithExtension,
      (stem, context) => {
        const parent: { document?: File } = context.parent;
        const referenceFileName = parent.document?.name ?? ownFileName;

        return !hasDisguisedExtension(stem, referenceFileName);
      }
    );

/**
 * The name, or the first ` (n)` variant no other document displays. For
 * uploads, which have no field to reject into and no moment to ask.
 */
export const toUniqueFileName = (
  fileName: string,
  existingFileNames?: string[]
): string => {
  if (!existingFileNames?.length) {
    return fileName;
  }

  const [stem, extension] = splitFileName(fileName);
  let candidate = fileName;
  let copyNumber = 1;
  while (isFileNameUsed(candidate, '', existingFileNames)) {
    copyNumber += 1;
    candidate = `${stem} (${copyNumber})${extension}`;
  }

  return candidate;
};

/**
 * One name per uploaded file, each clear of the names claimed earlier in the
 * same batch, so dropping one file twice lands two distinct cards.
 */
export const toUniqueFileNames = (
  fileNames: string[],
  existingFileNames?: string[]
): string[] => {
  const claimed = [...(existingFileNames ?? [])];

  return fileNames.map((fileName) => {
    const unique = toUniqueFileName(fileName, claimed);
    claimed.push(unique);
    return unique;
  });
};
